import { html, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { WEB_COMPONENT_GZ_RM_TOP_VULNERABILITIES_IMPACT_WIDGET } from '../../../../webcomponents.constants';
import { RmTopVulnerabilitiesByImpactPayload } from '@nirvana/shared/dtos/types/widgets/common';
import { renderNumber, renderNumberWithTooltip, renderText } from '../../text';
import { translateFn } from '../../../../utils/translator.helper';
import { DEFAULT_RM_WIDGET_TOP_ROWS } from '@nirvana/shared/constants';
import { Row, RowConfig } from '../../../../ui/gzBasicTable/types/types';
import { fillArray } from '@nirvana/shared/basic-types/array';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { LinkType } from '../../../../ui/gzLinkComponent/types';
import { FontSizes } from '../../../../types/common/types';
import { ColumnKeys as VulnerabilitiesColumnKeys } from '../../../vulnerabilitiesApp/types';
import { State } from '../../../common/constants';
import { computeMaxRowsFit, fitItemsToWidget } from '../../resize';

/**
 * @summary Type here the description of this component
 * @tag gz-rm-top-vulnerabilities-impact-widget
 */
@customElement(WEB_COMPONENT_GZ_RM_TOP_VULNERABILITIES_IMPACT_WIDGET)
export class GzRmTopVulnerabilitiesImpactWidgetWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true })
  payload: RmTopVulnerabilitiesByImpactPayload = { topVulnerabilitiesByImpact: { companyId: '', total: 0, items: [] } };

  static get styles() {
    return styles;
  }

  protected viewAll(): void {
    NavigationHelper.navigateToRiskManagement({
      company: { id: this.payload.topVulnerabilitiesByImpact?.companyId ?? '' },
      filters: [
        {
          [VulnerabilitiesColumnKeys.STATE]: { in: [State.ACTIVE] },
        },
        {
          [VulnerabilitiesColumnKeys.RISK_SCORE]: { range: { min: 1, max: 100 } },
        },
      ],
      section: 'vulnerabilities',
      newTab: true,
    });
  }

  protected renderLoader(): TemplateResult {
    return html` <gz-loader class="pt-1" .rows="${computeMaxRowsFit(this.contentHeight)}" columns="1"></gz-loader>`;
  }

  protected renderContent(): TemplateResult {
    const items: Row[] =
      this.payload.topVulnerabilitiesByImpact?.items?.map(
        ({ applicationName, affectedDevices, totalAffectedDevices }): RowConfig => ({
          data: { applicationName },
          cells: [
            renderText(applicationName),
            html` <gz-rm-icons-list
              iconSet="entity"
              iconClassMapType="devices"
              .items="${affectedDevices}"
            ></gz-rm-icons-list>`,
            renderNumberWithTooltip(totalAffectedDevices, translateFn('Total affected resources')),
          ],
        })
      ) ?? [];

    const emptyItem = [renderText('...', true), html` <gz-rm-icons-list></gz-rm-icons-list>`, renderNumber('--', true)];
    const fullItems: Row[] = fitItemsToWidget(fillArray(items, DEFAULT_RM_WIDGET_TOP_ROWS, emptyItem), this.contentHeight);

    return html`
      <gz-basic-table
        style="margin-top: calc(var(--spacer)*0.3)"
        rowsClickable
        alignLastColumnRight
        .onClick="${(rowData: { applicationName: string }) => {
          if (!rowData) {
            return;
          }

          const { applicationName } = rowData;

          NavigationHelper.navigateToRiskManagement({
            company: { id: this.payload.topVulnerabilitiesByImpact?.companyId ?? '' },
            section: 'vulnerabilities',
            filters: [
              {
                [VulnerabilitiesColumnKeys.NAME]: {
                  complexEnumFilter: [{ label: applicationName, value: applicationName }],
                },
              },
            ],
            newTab: true,
          });
        }}"
        .data="${fullItems}"
      ></gz-basic-table>

      <gz-link type="${LinkType.QUIET}" fontSize="${FontSizes.PARAGRAPH_11}" .callback=${() => this.viewAll()}>
        ${translateFn('View all')} (${this.payload.topVulnerabilitiesByImpact?.total ?? 0})
      </gz-link>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rm-top-vulnerabilities-impact-widget': GzRmTopVulnerabilitiesImpactWidgetWebComponent;
  }
}
